import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, BellOff, CheckCircle2, AlertTriangle } from 'lucide-react';
import { apiClient } from '../api/client';

/**
 * PulseMuted — Sprint 202
 * ───────────────────────
 * Landing page after tapping "Mute this section for 4 weeks" in the
 * Monday Pulse email. Calls the backend, shows confirmation + an
 * "Undo / unmute" link.
 *
 *  URL:  /pulse-muted?email=robert@hibff.com&section=magic_links
 */

const PURPLE = '#7B4FD8';

const PulseMuted = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const section = searchParams.get('section');
  const [phase, setPhase] = useState('loading');  // loading | done | error | unmuted
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!email || !section) {
      setPhase('error');
      setError('Missing email or section in the link.');
      return;
    }
    let cancelled = false;
    apiClient.request(`/api/admin/pulse/mute?email=${encodeURIComponent(email)}&section=${encodeURIComponent(section)}`)
      .then((res) => { if (!cancelled) { setData(res); setPhase('done'); } })
      .catch((err) => { if (!cancelled) { setError(err?.message || 'Could not mute.'); setPhase('error'); } });
    return () => { cancelled = true; };
  }, [email, section]);

  const unmute = async () => {
    try {
      await apiClient.request(`/api/admin/pulse/unmute?email=${encodeURIComponent(email)}&section=${encodeURIComponent(section)}`);
      setPhase('unmuted');
    } catch (err) {
      setError(err?.message || 'Could not unmute.');
    }
  };

  const expires = data?.expires_at ? new Date(data.expires_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-orange-50 py-12 px-4 flex items-center">
      <div className="max-w-md mx-auto bg-white rounded-3xl shadow-xl p-8 text-center w-full" data-testid="pulse-muted-page">
        <img src="/hibff-logo.png" alt="HiBFF" className="h-10 mx-auto mb-6" />

        {phase === 'loading' && (
          <Loader2 size={32} className="animate-spin mx-auto" style={{ color: PURPLE }} />
        )}

        {phase === 'done' && data && (
          <>
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-3" style={{ background: '#F5F3FF' }}>
              <BellOff size={24} style={{ color: PURPLE }} />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-1">Section muted</h1>
            <p className="text-gray-600 text-sm mb-4">
              The <strong>{data.section_label}</strong> section will be hidden from your Monday Pulse for the next {data.weeks} weeks.
            </p>
            {expires && (
              <p className="text-xs text-gray-500 mb-5">Auto-resumes on <strong>{expires}</strong>.</p>
            )}
            <button
              onClick={unmute}
              className="text-sm font-semibold underline"
              style={{ color: PURPLE }}
              data-testid="pulse-undo-mute"
            >
              Undo and resume now
            </button>
          </>
        )}

        {phase === 'unmuted' && (
          <>
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-3" style={{ background: '#ECFDF5' }}>
              <CheckCircle2 size={24} style={{ color: '#059669' }} />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-1">Unmuted</h1>
            <p className="text-gray-600 text-sm">You&apos;ll see this section again in next Monday&apos;s Pulse.</p>
          </>
        )}

        {phase === 'error' && (
          <>
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-3" style={{ background: '#FEF3F2' }}>
              <AlertTriangle size={24} style={{ color: '#B42318' }} />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-1">Something went wrong</h1>
            <p className="text-gray-600 text-sm">{error}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default PulseMuted;
